import type { AggregationOption } from "src/pages/metrics/constants";

type HeatmapData = {
  dimension1Values: string[];
  dimension2Values: string[];
  grid: {
    dimension1: string;
    cells: {
      value: number;
      numerator?: number;
      denominator?: number;
      aggregation?: AggregationOption;
      normalization?: string | null;
      heatmapGrouping?: {
        type: string;
        dimension: string;
        value: string;
        description?: string;
      }[];
    }[];
  }[];
  minValue: number;
  maxValue: number;
};

export type SortFn = (a: string, b: string) => number;

const defaultSortFn: SortFn = (a, b) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

export const generateHeatmapData = (
  seriesGroups: {
    seriesName: string;
    series: {
      data: {
        metricValue: number;
        metadata?: { numerator?: number; denominator?: number };
        normalization?: string | null;
        aggregation?: AggregationOption;
      }[];
      heatmapGrouping: {
        type: string;
        dimension: string;
        value: string;
        description?: string;
      }[];
    }[];
  }[],
  selectedSeriesName: string | undefined,
  axesFlipped: boolean,
  groupBySortFns: [SortFn, SortFn] = [defaultSortFn, defaultSortFn],
): HeatmapData => {
  const seriesData = seriesGroups.find(
    (group) => group.seriesName === selectedSeriesName,
  )?.series;

  if (!seriesData?.length) {
    return {
      dimension1Values: [],
      dimension2Values: [],
      grid: [],
      minValue: 0,
      maxValue: 0,
    };
  }

  // Create a cell for a given series
  const createCell = (matchingSeries: (typeof seriesData)[0] | undefined) => {
    return {
      value: matchingSeries?.data[0]?.metricValue ?? 0,
      numerator: matchingSeries?.data[0]?.metadata?.numerator,
      denominator: matchingSeries?.data[0]?.metadata?.denominator,
      aggregation: matchingSeries?.data[0]?.aggregation,
      normalization: matchingSeries?.data[0]?.normalization,
      heatmapGrouping: matchingSeries?.heatmapGrouping,
    };
  };

  // Find a series that matches the given dimension values
  const findMatchingSeries = (dim1Value: string, dim2Value?: string) => {
    return seriesData.find((series) => {
      if (!dim2Value) {
        return series.heatmapGrouping?.[0]?.value === dim1Value;
      }
      return axesFlipped
        ? series.heatmapGrouping?.[1]?.value === dim1Value &&
            series.heatmapGrouping?.[0]?.value === dim2Value
        : series.heatmapGrouping?.[0]?.value === dim1Value &&
            series.heatmapGrouping?.[1]?.value === dim2Value;
    });
  };

  // Get unique values for a given dimension
  const getUniqueValues = (dimensionIndex: 0 | 1) => {
    const values = new Set<string>();
    seriesData.forEach((row) => {
      values.add(row.heatmapGrouping?.[dimensionIndex]?.value ?? "");
    });

    const sortFn = groupBySortFns[dimensionIndex];
    return Array.from(values).sort(sortFn);
  };

  // Track min/max values across all cells
  let minValue = Infinity;
  let maxValue = -Infinity;
  const updateMinMax = (value: number) => {
    minValue = Math.min(minValue, value);
    maxValue = Math.max(maxValue, value);
  };

  const numDimensions = seriesData[0]?.heatmapGrouping?.length ?? 0;

  switch (numDimensions) {
    case 0: {
      const cell = createCell(seriesData[0]);
      updateMinMax(cell.value);
      return {
        dimension1Values: [""],
        dimension2Values: [selectedSeriesName ?? ""],
        grid: [{ dimension1: selectedSeriesName ?? "", cells: [cell] }],
        minValue: cell.value,
        maxValue: cell.value,
      };
    }

    case 1: {
      const values = getUniqueValues(0);
      const gridData = values.map((value) => {
        const cell = createCell(findMatchingSeries(value));
        updateMinMax(cell.value);
        return cell;
      });

      return axesFlipped
        ? {
            dimension1Values: values,
            dimension2Values: [selectedSeriesName ?? ""],
            grid: values.map((value, i) => ({
              dimension1: value,
              cells: [gridData[i]!],
            })),
            minValue,
            maxValue,
          }
        : {
            dimension1Values: [""],
            dimension2Values: values,
            grid: [{ dimension1: selectedSeriesName ?? "", cells: gridData }],
            minValue,
            maxValue,
          };
    }

    case 2: {
      const dim1Values = getUniqueValues(axesFlipped ? 1 : 0);
      const dim2Values = getUniqueValues(axesFlipped ? 0 : 1);

      const gridData = dim1Values.map((dim1) => ({
        dimension1: dim1,
        cells: dim2Values.map((dim2) => {
          const cell = createCell(findMatchingSeries(dim1, dim2));
          updateMinMax(cell.value);
          return cell;
        }),
      }));

      return {
        dimension1Values: dim1Values,
        dimension2Values: dim2Values,
        grid: gridData,
        minValue,
        maxValue,
      };
    }

    default:
      return {
        dimension1Values: [],
        dimension2Values: [],
        grid: [],
        minValue: 0,
        maxValue: 0,
      };
  }
};
